// Restricted-operator initial map layer defaults.
//
// Background: the global store defaults (useCountyLayerStore.countiesVisible
// = true, useGloStore.olsVisible = false, useLayersStore.assets.pads ON) are
// tuned for the full-shell user — county grid is the calmer reference, and
// pads are the headline asset. Restricted operators land on the PadRekko-only
// shell with a different job: they want the survey (GLO/OLS) grid as their
// spatial reference and a clean map with pads off until they open a case.
//
// Rather than fork the global store defaults (which would change every user's
// first load), flip just the restricted operator's starting state here — the
// same single-responsibility pattern as the other useRestricted* hooks.
//
// One-shot: applied once when isRestricted first resolves true. We don't keep
// re-asserting it, so if anything later toggles these layers it stays toggled
// (restricted users have no LayerPanel today, so in practice it just sets the
// initial state).
//
// County vs survey grid are mutually exclusive (see ReferenceTab) — setting
// county OFF + survey ON directly preserves that invariant (exactly one on).

import { useEffect, useRef } from "react";

import { useAuthStore, selectIsRestrictedOperator } from "../stores/useAuthStore";
import { useGloStore } from "../stores/useGloStore";
import { useCountyLayerStore } from "../stores/useCountyLayerStore";
import { useLayersStore } from "../stores/useLayersStore";

export function useRestrictedInitialLayers() {
  const isRestricted = useAuthStore(selectIsRestrictedOperator);
  const applied = useRef(false);

  useEffect(() => {
    if (!isRestricted) return;
    if (applied.current) return;
    applied.current = true;

    // Survey grid ON, county grid OFF (mutually exclusive reference grids).
    useGloStore.getState().setOlsVisible(true);
    useCountyLayerStore.getState().setCountiesVisible(false);

    // Pads OFF. PadLayer renders when assets.pads enabled && geometry.polygon.
    // toggleAssetGeometry('pads','polygon') flips polygon off and auto-derives
    // enabled off — the same path the Features-tab toggle uses. Guard on the
    // current value so this is a one-shot OFF, not a blind toggle.
    const pads = useLayersStore.getState().assets?.pads;
    if (pads?.geometry?.polygon) {
      useLayersStore.getState().toggleAssetGeometry("pads", "polygon");
    }
  }, [isRestricted]);
}
